import { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { useStateMachine } from "little-state-machine";
import { updateAddressBookData } from "store/store";

import { getCompanyAddressBook, getCompanyAddressV2Book, getCompanyAddressV2Details } from "api";
import { Address } from "api/models";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { getScheduleAddressReferenceField, mergeSelectedAddressIntoBook } from "../PickupDeliveryAddress/helpers";

interface useScheduleAddressProps {
  typeKey: string;
}

export default function useScheduleAddress({ typeKey }: useScheduleAddressProps) {
  const intl = useIntl();
  const { getValues, watch } = useFormContext();

  const [addressBook, setAddressBook] = useState<Address[]>([]);
  const [searchPlace, setSearchPlace] = useState(getValues(`schedule.${typeKey}_address_name`) || "");
  const selectedAddressDetailsRef = useRef<Address | null>(null);
  const [isHydratingSelectedAddress, setIsHydratingSelectedAddress] = useState(() =>
    Boolean(getValues(getScheduleAddressReferenceField(typeKey))),
  );

  const { state, actions } = useStateMachine({ updateAddressBookData });

  const { permissions } = usePermissions();
  const { enable_new_address_structure } = permissions ? JSON.parse(permissions)?.company_shipper_settings : null;

  const getAddressBookAPI: any = enable_new_address_structure ? getCompanyAddressV2Book : getCompanyAddressBook;

  const location = getValues("organizational.location") || getValues("shipment.location");
  const selectedAddressReference = watch(getScheduleAddressReferenceField(typeKey));

  const applyAddressBook = (results: Address[] = []) => {
    const nextResults = mergeSelectedAddressIntoBook(results, selectedAddressDetailsRef.current);
    actions.updateAddressBookData({
      addressBook: nextResults,
    });
    setAddressBook(nextResults);
  };

  const getAddresses = () => {
    if (!location) return;
    if (searchPlace?.length > 0) return;
    getAddressBookAPI({
      params: {
        payment_location: location,
        page_size: 20,
      },
    })
      .then((res: AxiosResponse) => {
        applyAddressBook(res.data.results ?? []);
      })
      .catch((err: any) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.schedule.couldNotRetrieveAddressBook",
            defaultMessage: "Could not retrieve address book. Please try again later.",
          }),
        ),
      );
  };

  useEffect(() => {
    getAddresses();
  }, [watch("organizational.location"), watch("shipment.location")]);

  useEffect(() => {
    const addressSearchAbortController = new AbortController();
    if (searchPlace === "") {
      applyAddressBook(state?.addressBook ?? []);
    }
    if (!location) return undefined;
    const timer = setTimeout(() => {
      getAddressBookAPI({
        params: {
          payment_location: location,
          query: searchPlace,
          page_size: 20,
        },
        signal: addressSearchAbortController.signal,
      }).then((res: any) => applyAddressBook(res.data?.results));
    }, 300);
    return () => {
      clearTimeout(timer);
      addressSearchAbortController.abort();
    };
  }, [searchPlace]);

  useEffect(() => {
    if (!enable_new_address_structure || !selectedAddressReference) {
      selectedAddressDetailsRef.current = null;
      setIsHydratingSelectedAddress(false);
      return undefined;
    }
    if (selectedAddressDetailsRef.current?.reference_number === selectedAddressReference) {
      setIsHydratingSelectedAddress(false);
      return undefined;
    }

    let cancelled = false;
    setIsHydratingSelectedAddress(true);
    getCompanyAddressV2Details(selectedAddressReference)
      .then((res) => {
        if (cancelled || !res?.data) return;
        selectedAddressDetailsRef.current = res.data as Address;
        setAddressBook((prev) => {
          const nextResults = mergeSelectedAddressIntoBook(prev, res.data as Address);
          actions.updateAddressBookData({ addressBook: nextResults });
          return nextResults;
        });
      })
      .catch(() => {
        if (!cancelled) selectedAddressDetailsRef.current = null;
      })
      .finally(() => {
        if (!cancelled) setIsHydratingSelectedAddress(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressReference, enable_new_address_structure]);

  return { addressBook, searchPlace, setSearchPlace, getAddresses, isHydratingSelectedAddress };
}
